import { ChangeDetectionStrategy, Component, HostListener, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';

import { AuthService } from '../../core/auth/auth.service';
import { FeedbackKind } from '../../core/feedback/feedback.types';
import { IconComponent } from '../icon/icon';
import { ReportDialogComponent, ReportDialogData } from '../report-dialog/report-dialog';

/**
 * The floating "Report" launcher pinned to the bottom-right of every page.
 *
 * Renders only for a signed-in user: a report is only worth acting on if we
 * know who filed it and can reply to them, and the endpoint requires
 * authentication in any case.
 *
 * It is a menu rather than a single button so the choice of "broken" versus
 * "suggestion" is made before the form opens — the two get triaged
 * differently, and asking inside the form buries it.
 */
@Component({
  selector: 'app-report-widget',
  imports: [MatButtonModule, MatTooltipModule, IconComponent],
  templateUrl: './report-widget.html',
  styleUrl: './report-widget.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportWidgetComponent {
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly auth = inject(AuthService);

  protected readonly isAuthenticated = this.auth.isAuthenticated;
  protected readonly isOpen = signal(false);

  protected toggle(): void {
    this.isOpen.update((open) => !open);
  }

  /** Close on Escape, matching every other dismissable surface. */
  @HostListener('document:keydown.escape')
  protected close(): void {
    this.isOpen.set(false);
  }

  /** Close when a click lands outside the widget. */
  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen()) return;
    const host = (event.target as HTMLElement).closest('app-report-widget');
    if (!host) this.isOpen.set(false);
  }

  protected openReport(kind: FeedbackKind): void {
    this.isOpen.set(false);
    this.dialog
      .open<ReportDialogComponent, ReportDialogData, string | null>(ReportDialogComponent, {
        width: '560px',
        maxWidth: '95vw',
        autoFocus: false,
        // Only the dialog's own Close/Cancel may dismiss it. A stray backdrop
        // click or Escape discarded the typed report without a word — and
        // while a screenshot is being taken the dialog is invisible but its
        // backdrop is not, so clicking the page threw the report away.
        disableClose: true,
        data: { kind },
      })
      .afterClosed()
      .subscribe((reference) => {
        if (!reference) return;
        this.snackBar.open(`Thanks — your report is with us (${reference}).`, 'Dismiss', {
          duration: 6000,
        });
      });
  }
}
